import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  DollarSign,
  Gauge,
  Activity,
  Megaphone,
  FileStack,
  Mail,
  Smartphone,
  ArrowLeft,
  LogOut,
  X,
} from 'lucide-react';
import { ICON_URL } from '../../constants';
import { useAuth } from '../../hooks/useAuth';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';

const adminMenuItems = [
  { icon: LayoutDashboard, label: 'Overview', href: '/admin/overview', enabled: true },
  { icon: Users, label: 'Usuários', href: '/admin/users', enabled: true },
  { icon: DollarSign, label: 'Receita', href: '/admin/revenue', enabled: true },
  { icon: Gauge, label: 'Plans Admin', href: '/admin/plans', enabled: true },
  { icon: Activity, label: 'Logs', href: '/admin/logs', enabled: true },
  { icon: Megaphone, label: 'Marketing', href: '/admin/marketing', enabled: true },
  { icon: FileStack, label: 'Templates', href: '/admin/templates', enabled: true },
  { icon: Mail, label: 'Email', href: '/admin/email', enabled: true },
  { icon: Smartphone, label: 'Mobile Builder', href: '/admin/mobile-builder', enabled: true },
];

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled])';

interface AdminSidebarProps {
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ isMobileOpen, onMobileClose }) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const asideRef = useRef<HTMLElement>(null);

  useLockBodyScroll(isMobileOpen);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      setSigningOut(false);
    }
  };

  const handleBackToDashboard = () => {
    onMobileClose();
    navigate('/dashboard');
  };

  useEffect(() => {
    if (!isMobileOpen) return;

    const focusables = asideRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    // Focus first NavLink (index 1), falling back to close button (index 0)
    const initialTarget = focusables?.[1] ?? focusables?.[0];
    initialTarget?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onMobileClose();
        return;
      }
      if (e.key === 'Tab' && asideRef.current) {
        const current = asideRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (current.length === 0) return;
        const first = current[0];
        const last = current[current.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileOpen, onMobileClose]);

  return (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        ref={asideRef}
        id="admin-drawer"
        role={isMobileOpen ? 'dialog' : undefined}
        aria-modal={isMobileOpen ? true : undefined}
        aria-label={isMobileOpen ? 'Menu de navegação admin' : undefined}
        className={`fixed top-0 left-0 h-full w-64 z-40 bg-neutral-950 border-r border-white/10 flex flex-col transition-transform duration-300 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={ICON_URL} alt="OneForm" className="w-8 h-8" />
            <div>
              <span className="text-sm font-semibold text-white">OneForm</span>
              <span className="text-xs text-red-400 ml-1">Admin</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onMobileClose}
            className="p-3 -mr-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5 lg:hidden"
            aria-label="Fechar menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {adminMenuItems.map(item =>
            item.enabled ? (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={onMobileClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 lg:py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-white/10 text-white'
                      : 'text-neutral-400 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ) : (
              <span
                key={item.href}
                className="flex items-center gap-3 px-3 py-3 lg:py-2 rounded-lg text-sm text-neutral-600 cursor-not-allowed"
              >
                <item.icon className="w-4 h-4" />
                {item.label}
                <span className="text-[10px] ml-auto">Em breve</span>
              </span>
            )
          )}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-1">
          <button
            type="button"
            onClick={handleBackToDashboard}
            className="flex items-center gap-3 px-3 py-3 lg:py-2 rounded-lg text-sm text-neutral-400 hover:text-white hover:bg-white/5 w-full"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Dashboard
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            className="flex items-center gap-3 px-3 py-3 lg:py-2 rounded-lg text-sm text-neutral-400 hover:text-red-400 hover:bg-white/5 w-full disabled:opacity-50"
          >
            <LogOut className="w-4 h-4" />
            {signingOut ? 'Saindo...' : 'Sair'}
          </button>
        </div>
      </aside>
    </>
  );
};
